import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Button, IconButton, MenuItem, Select, FormControl, InputLabel, Stack,
  Snackbar, Alert, Autocomplete, CircularProgress, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import * as XLSX from 'xlsx';
import apiService from '../services/api.service';

interface SettingRow { settingkey: string; settingvalue: string; description: string | null; }
interface ThresholdRow { thresholdid: number; scopetype: string; scopecode: string; maxdiscountpct: number; }
interface Salesperson { userid: number; fullname: string; username: string; role: string; }
interface AssignmentRow { assignmentid: number; cardcode: string; cardname: string | null; }
interface SapCustomer { cardCode: string; cardName: string; }
interface ImportError { row: number; username: string; cardCode: string; message: string; }
interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  customerValidation: 'checked' | 'skipped-not-synced';
  errors: ImportError[];
}
interface PriceListOpt { priceListNo: number; priceListName: string; }
interface PriceListRow { priceListNo: number; priceListName: string; }
interface PriceItemRow { itemCode: string; itemName: string; price: number | null; currency?: string; }

const SCOPE_TYPES = ['Customer', 'Item', 'CustomerGroup', 'ProductGroup', 'Role'] as const;

/**
 * LAST_PRICE_PRECEDENCE is a `_THEN_`-joined list of steps the backend walks in
 * order, taking the first price that resolves. Offered as a picker because an
 * unrecognised token is silently skipped server-side — a typo would quietly drop
 * a pricing step instead of erroring. LASTORDER/LASTINVOICE are always scoped to
 * the customer on the order, never the item's last price to anyone.
 */
const PRICE_PRECEDENCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'LASTORDER_THEN_PRICELIST', label: 'Last sales order (this customer) → Price list (default)' },
  { value: 'SPECIAL_THEN_PRICELIST_THEN_LASTINVOICE', label: 'Special price → Price list → Last invoice' },
  { value: 'SPECIAL_THEN_LASTORDER_THEN_PRICELIST', label: 'Special price → Last sales order (this customer) → Price list' },
  { value: 'LASTORDER_THEN_LASTINVOICE_THEN_PRICELIST', label: 'Last sales order → Last invoice → Price list' },
  { value: 'LASTINVOICE_THEN_PRICELIST', label: 'Last invoice (this customer) → Price list' },
  { value: 'PRICELIST', label: 'Price list only' },
];

function SalesPortalSettings() {
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);

  const notify = useCallback((msg: string, sev: 'success' | 'error' = 'success') => setToast({ msg, sev }), []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Sales Portal Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Governance thresholds are DB-driven — change them here, no code deploy needed.
      </Typography>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Governance Settings" />
          <Tab label="Discount Thresholds" />
          <Tab label="Customer Assignments" />
          <Tab label="Price Lists" />
        </Tabs>
        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
          {tab === 0 && <SettingsTab companyId={companyId} notify={notify} />}
          {tab === 1 && <ThresholdsTab companyId={companyId} notify={notify} />}
          {tab === 2 && <AssignmentsTab companyId={companyId} notify={notify} />}
          {tab === 3 && <PriceListsTab companyId={companyId} notify={notify} />}
        </Box>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// ---------------------------------------------------------------------------
function SettingsTab({ companyId, notify }: { companyId?: number; notify: (m: string, s?: 'success' | 'error') => void }) {
  const [rows, setRows] = useState<SettingRow[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [priceLists, setPriceLists] = useState<PriceListOpt[]>([]);
  const [plLoading, setPlLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await apiService.getSalesSettings(companyId);
      setRows(res.data || []);
    } catch (e: any) {
      notify(e.response?.data?.message || 'Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, notify]);

  useEffect(() => { void load(); }, [load]);

  // SAP price lists for the FLOOR_PRICE_PRICELIST_NO picker (per-company SAP read).
  useEffect(() => {
    if (!companyId) { setPriceLists([]); return; }
    let cancelled = false;
    setPlLoading(true);
    (async () => {
      try {
        const res: any = await apiService.getSalesPriceLists(companyId);
        if (!cancelled) setPriceLists(res.data || []);
      } catch { if (!cancelled) setPriceLists([]); }
      finally { if (!cancelled) setPlLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const save = async (key: string) => {
    try {
      await apiService.updateSalesSetting(key, edits[key], companyId);
      notify('Setting saved');
      setEdits((prev) => { const n = { ...prev }; delete n[key]; return n; });
      await load();
    } catch (e: any) {
      notify(e.response?.data?.message || 'Save failed', 'error');
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Setting</TableCell>
          <TableCell>Value</TableCell>
          <TableCell>Description</TableCell>
          <TableCell align="right">Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          const dirty = edits[r.settingkey] !== undefined && edits[r.settingkey] !== r.settingvalue;
          return (
            <TableRow key={r.settingkey}>
              <TableCell><code>{r.settingkey}</code></TableCell>
              <TableCell sx={{ minWidth: 200 }}>
                {r.settingkey === 'FLOOR_PRICE_PRICELIST_NO' && companyId ? (
                  <PriceListSettingField
                    value={edits[r.settingkey] ?? r.settingvalue ?? ''}
                    options={priceLists}
                    loading={plLoading}
                    onChange={(v) => setEdits((prev) => ({ ...prev, [r.settingkey]: v }))}
                  />
                ) : r.settingkey === 'LAST_PRICE_PRECEDENCE' ? (
                  <PrecedenceSettingField
                    value={edits[r.settingkey] ?? r.settingvalue ?? ''}
                    onChange={(v) => setEdits((prev) => ({ ...prev, [r.settingkey]: v }))}
                  />
                ) : r.settingkey === 'STOCK_CATEGORY_FIELD' ? (
                  <FormControl size="small" fullWidth>
                    <Select
                      value={edits[r.settingkey] ?? r.settingvalue ?? 'ItemGroup'}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [r.settingkey]: String(e.target.value) }))}
                    >
                      <MenuItem value="ItemGroup">Item Group (SAP)</MenuItem>
                      <MenuItem value="U_Item_SubGrp1">U_Item_SubGrp1</MenuItem>
                      <MenuItem value="U_CCName">U_CCName</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    value={edits[r.settingkey] ?? r.settingvalue}
                    onChange={(e) => setEdits((prev) => ({ ...prev, [r.settingkey]: e.target.value }))}
                  />
                )}
              </TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{r.description}</Typography></TableCell>
              <TableCell align="right">
                <Tooltip title="Save">
                  <span>
                    <IconButton size="small" color="primary" disabled={!dirty} onClick={() => save(r.settingkey)}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
function ThresholdsTab({ companyId, notify }: { companyId?: number; notify: (m: string, s?: 'success' | 'error') => void }) {
  const [rows, setRows] = useState<ThresholdRow[]>([]);
  const [scopeType, setScopeType] = useState<string>('Customer');
  const [scopeCode, setScopeCode] = useState('');
  const [maxPct, setMaxPct] = useState('');

  const load = useCallback(async () => {
    try {
      const res: any = await apiService.getSalesThresholds(companyId);
      setRows(res.data || []);
    } catch (e: any) {
      notify(e.response?.data?.message || 'Failed to load thresholds', 'error');
    }
  }, [companyId, notify]);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    const pct = Number(maxPct);
    if (!scopeCode || !Number.isFinite(pct) || pct < 0) { notify('Enter a scope code and a valid %', 'error'); return; }
    try {
      await apiService.upsertSalesThreshold({ scopeType, scopeCode, maxDiscountPct: pct, companyId: companyId ?? null });
      setScopeCode(''); setMaxPct('');
      notify('Threshold saved');
      await load();
    } catch (e: any) {
      notify(e.response?.data?.message || 'Save failed', 'error');
    }
  };

  const remove = async (id: number) => {
    try { await apiService.deleteSalesThreshold(id); await load(); notify('Threshold removed'); }
    catch (e: any) { notify(e.response?.data?.message || 'Delete failed', 'error'); }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="scope-type">Scope</InputLabel>
          <Select labelId="scope-type" label="Scope" value={scopeType} onChange={(e) => setScopeType(String(e.target.value))}>
            {SCOPE_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Scope Code" value={scopeCode} onChange={(e) => setScopeCode(e.target.value)} />
        <TextField size="small" label="Max Discount %" type="number" value={maxPct} onChange={(e) => setMaxPct(e.target.value)} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={add}>Add</Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Scope</TableCell>
            <TableCell>Code</TableCell>
            <TableCell align="right">Max Discount %</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">No thresholds — the default applies.</Typography></TableCell></TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.thresholdid}>
              <TableCell>{r.scopetype}</TableCell>
              <TableCell>{r.scopecode}</TableCell>
              <TableCell align="right">{Number(r.maxdiscountpct)}</TableCell>
              <TableCell align="right">
                <IconButton size="small" color="error" onClick={() => remove(r.thresholdid)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
function AssignmentsTab({ companyId, notify }: { companyId?: number; notify: (m: string, s?: 'success' | 'error') => void }) {
  const [people, setPeople] = useState<Salesperson[]>([]);
  const [userId, setUserId] = useState<number | ''>('');
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [customerOptions, setCustomerOptions] = useState<SapCustomer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<SapCustomer | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    (async () => {
      try { const res: any = await apiService.getSalespeople(); setPeople(res.data || []); }
      catch (e: any) { notify(e.response?.data?.message || 'Failed to load salespeople', 'error'); }
    })();
  }, [notify]);

  const loadAssignments = useCallback(async (uid: number) => {
    try { const res: any = await apiService.getSalesAssignments(uid, companyId); setAssignments(res.data || []); }
    catch (e: any) { notify(e.response?.data?.message || 'Failed to load assignments', 'error'); }
  }, [companyId, notify]);

  useEffect(() => { if (typeof userId === 'number') void loadAssignments(userId); }, [userId, loadAssignments]);

  // Debounced SAP customer search.
  useEffect(() => {
    if (!companyId || customerSearch.trim().length < 2) { setCustomerOptions([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res: any = await apiService.getSalesCustomers(companyId, customerSearch.trim());
        if (!cancelled) setCustomerOptions(res.data || []);
      } catch { if (!cancelled) setCustomerOptions([]); }
      finally { if (!cancelled) setSearching(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [customerSearch, companyId]);

  const assign = async () => {
    if (typeof userId !== 'number' || !companyId || !selectedCustomer) { notify('Pick a salesperson and a customer', 'error'); return; }
    try {
      await apiService.assignSalesCustomer({ userId, companyId, cardCode: selectedCustomer.cardCode, cardName: selectedCustomer.cardName });
      setSelectedCustomer(null); setCustomerSearch('');
      notify('Customer assigned');
      await loadAssignments(userId);
    } catch (e: any) { notify(e.response?.data?.message || 'Assign failed', 'error'); }
  };

  const remove = async (id: number) => {
    try { await apiService.removeSalesAssignment(id); if (typeof userId === 'number') await loadAssignments(userId); notify('Assignment removed'); }
    catch (e: any) { notify(e.response?.data?.message || 'Remove failed', 'error'); }
  };

  // ---- Bulk import ---------------------------------------------------------
  // The template ships three sheets: the one that gets filled in, a copy-ready
  // list of valid usernames (the #1 cause of rejected rows), and instructions.
  const downloadTemplate = () => {
    const sample = people[0]?.username || 'jdoe';
    const sample2 = people[1]?.username || people[0]?.username || 'asmith';

    const assignments = XLSX.utils.json_to_sheet([
      { Username: sample, 'Salesperson Name': people[0]?.fullname || 'John Doe', CardCode: 'C000123', 'Customer Name': 'Example Trading Co.' },
      { Username: sample2, 'Salesperson Name': people[1]?.fullname || 'Alice Smith', CardCode: 'C000456', 'Customer Name': 'Example Contracting Est.' },
    ]);
    assignments['!cols'] = [{ wch: 20 }, { wch: 28 }, { wch: 16 }, { wch: 36 }];

    const roster = XLSX.utils.json_to_sheet(
      people.length
        ? people.map((p) => ({ Username: p.username, 'Full Name': p.fullname || '', Role: p.role }))
        : [{ Username: '(no salespeople found)', 'Full Name': '', Role: '' }]
    );
    roster['!cols'] = [{ wch: 20 }, { wch: 28 }, { wch: 18 }];

    const instructions = XLSX.utils.aoa_to_sheet([
      ['Bulk customer assignment — how to use this template'],
      [],
      ['1.', 'Fill in the "Assignments" sheet. One row per salesperson + customer pair.'],
      ['2.', 'Delete the two example rows before uploading.'],
      ['3.', 'Username must match a user on the "Valid Salespeople" sheet exactly (case does not matter).'],
      ['', 'A user only appears there if they are active AND their role is Salesperson or Sales Management.'],
      ['4.', 'CardCode is the SAP B1 customer code (BP CardCode). It is checked against this company\'s synced customers.'],
      ['5.', 'Customer Name is optional — leave it blank and the SAP name is filled in automatically.'],
      ['6.', 'Assignments are per-company. This file applies to the company selected in the top bar.'],
      ['7.', 'Re-uploading a pair that already exists is safe — it refreshes it, it does not duplicate.'],
      [],
      ['Columns on the "Assignments" sheet:'],
      ['Username', 'Required. The portal login username of the salesperson.'],
      ['Salesperson Name', 'Optional. Ignored on import — for your reference only.'],
      ['CardCode', 'Required. SAP B1 customer code.'],
      ['Customer Name', 'Optional. Ignored if the code resolves in SAP.'],
    ]);
    instructions['!cols'] = [{ wch: 20 }, { wch: 96 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, assignments, 'Assignments');
    XLSX.utils.book_append_sheet(wb, roster, 'Valid Salespeople');
    XLSX.utils.book_append_sheet(wb, instructions, 'Instructions');
    XLSX.writeFile(wb, 'Customer_Assignment_Template.xlsx');
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file after a fix
    if (!file || !companyId) return;
    // Captured as a const so the narrowing survives into the reader callback.
    const targetCompanyId = companyId;

    setImporting(true);
    setImportResult(null);
    const reader = new FileReader();
    reader.onerror = () => { setImporting(false); notify('Could not read the file', 'error'); };
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: 'array' });
        const sheetName = wb.SheetNames.includes('Assignments') ? 'Assignments' : wb.SheetNames[0];
        const json = XLSX.utils.sheet_to_json(wb.Sheets[sheetName]) as any[];

        const pick = (row: any, ...keys: string[]) => {
          for (const k of keys) {
            const hit = Object.keys(row).find((c) => c.trim().toLowerCase() === k.toLowerCase());
            if (hit && row[hit] != null && String(row[hit]).trim() !== '') return String(row[hit]).trim();
          }
          return '';
        };

        const rows = json
          .map((r, i) => {
            const rawId = pick(r, 'UserId', 'User Id');
            return {
              rowNum: i + 2, // sheet row: header is row 1
              username: pick(r, 'Username', 'User Name', 'User'),
              userId: rawId && Number.isFinite(Number(rawId)) ? Number(rawId) : undefined,
              cardCode: pick(r, 'CardCode', 'Card Code', 'Customer Code'),
              cardName: pick(r, 'Customer Name', 'CardName', 'Card Name') || undefined,
            };
          })
          .filter((r) => r.username || r.userId != null || r.cardCode); // drop blank rows

        if (rows.length === 0) {
          notify(`No rows found on sheet "${sheetName}"`, 'error');
          return;
        }

        const res: any = await apiService.bulkAssignSalesCustomers({ companyId: targetCompanyId, rows });
        const data: ImportResult = res.data;
        setImportResult(data);
        notify(res.message || 'Import complete', data.skipped > 0 ? 'error' : 'success');
        if (typeof userId === 'number') await loadAssignments(userId);
      } catch (err: any) {
        notify(err.response?.data?.message || err.message || 'Import failed', 'error');
      } finally {
        setImporting(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Rejected rows go back out as a workbook so the admin fixes and re-uploads
  // just those, rather than hunting through the original file.
  const downloadErrors = () => {
    if (!importResult?.errors.length) return;
    const ws = XLSX.utils.json_to_sheet(
      importResult.errors.map((x) => ({ 'Sheet Row': x.row, Username: x.username, CardCode: x.cardCode, Reason: x.message }))
    );
    ws['!cols'] = [{ wch: 10 }, { wch: 20 }, { wch: 16 }, { wch: 60 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rejected Rows');
    XLSX.writeFile(wb, 'Customer_Assignment_Errors.xlsx');
  };

  if (!companyId) {
    return <Alert severity="info">Select a company (top bar) to manage customer assignments — assignments are company-scoped.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Bulk import</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Assign many customers at once for the selected company. Download the template, fill in the
          Assignments sheet, then upload it. Existing pairs are refreshed, not duplicated.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
            Download template
          </Button>
          <Button
            variant="contained"
            component="label"
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            disabled={importing}
          >
            {importing ? 'Importing…' : 'Upload Excel'}
            <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleImport} disabled={importing} />
          </Button>
        </Stack>

        {importResult && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={importResult.skipped > 0 ? 'warning' : 'success'}>
              {importResult.created} newly assigned · {importResult.updated} already assigned (refreshed) ·{' '}
              {importResult.skipped} skipped
              {importResult.customerValidation === 'skipped-not-synced' && (
                <Typography variant="caption" display="block">
                  Card codes were not verified — this company has no synced SAP customers yet. Run the customer
                  sync to catch typos on the next import.
                </Typography>
              )}
            </Alert>

            {importResult.errors.length > 0 && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Skipped rows{importResult.errors.length > 50 ? ` (showing first 50 of ${importResult.errors.length})` : ''}
                  </Typography>
                  <Button size="small" startIcon={<DownloadIcon />} onClick={downloadErrors}>
                    Download skipped rows
                  </Button>
                </Stack>
                <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Card Code</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.errors.slice(0, 50).map((x, i) => (
                        <TableRow key={`${x.row}-${i}`}>
                          <TableCell>{x.row}</TableCell>
                          <TableCell>{x.username}</TableCell>
                          <TableCell>{x.cardCode}</TableCell>
                          <TableCell>{x.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>

      <FormControl size="small" sx={{ maxWidth: 360 }}>
        <InputLabel id="sp">Salesperson</InputLabel>
        <Select labelId="sp" label="Salesperson" value={userId} onChange={(e) => setUserId(Number(e.target.value))}>
          {people.map((p) => <MenuItem key={p.userid} value={p.userid}>{p.fullname || p.username} ({p.role})</MenuItem>)}
        </Select>
      </FormControl>

      {typeof userId === 'number' && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Autocomplete
              sx={{ minWidth: 320 }}
              size="small"
              options={customerOptions}
              loading={searching}
              value={selectedCustomer}
              onChange={(_, v) => setSelectedCustomer(v)}
              onInputChange={(_, v) => setCustomerSearch(v)}
              getOptionLabel={(o) => `${o.cardName} (${o.cardCode})`}
              isOptionEqualToValue={(a, b) => a.cardCode === b.cardCode}
              renderInput={(params) => (
                <TextField {...params} label="Search SAP customer (min 2 chars)" />
              )}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={assign} disabled={!selectedCustomer}>Assign</Button>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Card Code</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.length === 0 && (
                <TableRow><TableCell colSpan={3}><Typography variant="caption" color="text.secondary">No customers assigned.</Typography></TableCell></TableRow>
              )}
              {assignments.map((a) => (
                <TableRow key={a.assignmentid}>
                  <TableCell>{a.cardcode}</TableCell>
                  <TableCell>{a.cardname}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => remove(a.assignmentid)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Price-list maintenance: create/rename/delete price lists and edit item prices,
// written back to SAP B1. Browse is from the local sync (fast); each save writes
// to SAP authoritatively (read-modify-write preserves other lists' prices).
function PriceListsTab({ companyId, notify }: { companyId?: number; notify: (m: string, s?: 'success' | 'error') => void }) {
  const [lists, setLists] = useState<PriceListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [renamingNo, setRenamingNo] = useState<number | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [busy, setBusy] = useState(false);

  const [selected, setSelected] = useState<PriceListRow | null>(null);
  const [items, setItems] = useState<PriceItemRow[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [priceEdits, setPriceEdits] = useState<Record<string, string>>({});
  const [addCode, setAddCode] = useState('');
  const [addPrice, setAddPrice] = useState('');

  const loadLists = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try { const res: any = await apiService.getSalesPriceLists(companyId); setLists(res.data || []); }
    catch (e: any) { notify(e.response?.data?.message || 'Failed to load price lists', 'error'); }
    finally { setLoading(false); }
  }, [companyId, notify]);
  useEffect(() => { void loadLists(); }, [loadLists]);

  const loadItems = useCallback(async (no: number, term?: string) => {
    if (!companyId) return;
    setItemsLoading(true);
    try { const res: any = await apiService.getSalesPriceListItems(no, companyId, term); setItems(res.data || []); setPriceEdits({}); }
    catch (e: any) { notify(e.response?.data?.message || 'Failed to load prices', 'error'); }
    finally { setItemsLoading(false); }
  }, [companyId, notify]);

  // Debounced search within the selected list's items.
  useEffect(() => {
    if (!selected) return;
    const t = setTimeout(() => { void loadItems(selected.priceListNo, search.trim() || undefined); }, 350);
    return () => clearTimeout(t);
  }, [search, selected, loadItems]);

  const create = async () => {
    const name = newName.trim();
    if (!name) { notify('Enter a price list name', 'error'); return; }
    setBusy(true);
    try { await apiService.createSalesPriceList({ companyId, priceListName: name }); setNewName(''); notify('Price list created'); await loadLists(); }
    catch (e: any) { notify(e.response?.data?.message || 'Create failed', 'error'); }
    finally { setBusy(false); }
  };

  const rename = async (no: number) => {
    const name = renameVal.trim();
    if (!name) { notify('Enter a name', 'error'); return; }
    setBusy(true);
    try {
      await apiService.updateSalesPriceList(no, { companyId, priceListName: name });
      setRenamingNo(null); notify('Renamed');
      if (selected?.priceListNo === no) setSelected({ priceListNo: no, priceListName: name });
      await loadLists();
    } catch (e: any) { notify(e.response?.data?.message || 'Rename failed', 'error'); }
    finally { setBusy(false); }
  };

  const remove = async (row: PriceListRow) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete price list "${row.priceListName}" (${row.priceListNo})? This deletes it in SAP B1.`)) return;
    setBusy(true);
    try {
      await apiService.deleteSalesPriceList(row.priceListNo, companyId);
      notify('Price list deleted');
      if (selected?.priceListNo === row.priceListNo) { setSelected(null); setItems([]); }
      await loadLists();
    } catch (e: any) { notify(e.response?.data?.message || 'Delete failed', 'error'); }
    finally { setBusy(false); }
  };

  const savePrice = async (itemCode: string) => {
    if (!selected) return;
    const raw = priceEdits[itemCode];
    const price = Number(raw);
    if (raw === undefined || !Number.isFinite(price) || price < 0) { notify('Enter a valid price', 'error'); return; }
    setBusy(true);
    try {
      await apiService.setSalesItemPrice(selected.priceListNo, itemCode, { companyId, price });
      notify('Price updated in SAP');
      setItems((prev) => prev.map((it) => (it.itemCode === itemCode ? { ...it, price } : it)));
      setPriceEdits((prev) => { const n = { ...prev }; delete n[itemCode]; return n; });
    } catch (e: any) { notify(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setBusy(false); }
  };

  const addItemPrice = async () => {
    if (!selected) return;
    const code = addCode.trim();
    const price = Number(addPrice);
    if (!code || !Number.isFinite(price) || price < 0) { notify('Enter an item code and a valid price', 'error'); return; }
    setBusy(true);
    try {
      await apiService.setSalesItemPrice(selected.priceListNo, code, { companyId, price });
      setAddCode(''); setAddPrice(''); notify('Price set in SAP');
      await loadItems(selected.priceListNo, search.trim() || undefined);
    } catch (e: any) { notify(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setBusy(false); }
  };

  if (!companyId) return <Alert severity="info">Select a company (top bar) to maintain price lists — they are company-scoped in SAP.</Alert>;
  if (loading) return <CircularProgress size={24} />;

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-end' }}>
        <TextField size="small" label="New price list name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={create} disabled={busy}>Create price list</Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow><TableCell>Price List</TableCell><TableCell>No</TableCell><TableCell align="right">Actions</TableCell></TableRow>
        </TableHead>
        <TableBody>
          {lists.length === 0 && (
            <TableRow><TableCell colSpan={3}><Typography variant="caption" color="text.secondary">No price lists.</Typography></TableCell></TableRow>
          )}
          {lists.map((r) => (
            <TableRow key={r.priceListNo} selected={selected?.priceListNo === r.priceListNo}>
              <TableCell>
                {renamingNo === r.priceListNo ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField size="small" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} />
                    <Button size="small" onClick={() => rename(r.priceListNo)} disabled={busy}>Save</Button>
                    <Button size="small" color="inherit" onClick={() => setRenamingNo(null)}>Cancel</Button>
                  </Stack>
                ) : r.priceListName}
              </TableCell>
              <TableCell>{r.priceListNo}</TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => { setSelected(r); setSearch(''); void loadItems(r.priceListNo); }}>Prices</Button>
                <Tooltip title="Rename"><IconButton size="small" onClick={() => { setRenamingNo(r.priceListNo); setRenameVal(r.priceListName); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {selected && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle1">Prices in {selected.priceListName} ({selected.priceListNo})</Typography>
          <Typography variant="caption" color="text.secondary">Prices shown are from the last sync; saving writes directly to SAP B1.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ my: 1 }} alignItems={{ sm: 'flex-end' }}>
            <TextField size="small" label="Item code" value={addCode} onChange={(e) => setAddCode(e.target.value)} />
            <TextField size="small" label="Price" type="number" value={addPrice} onChange={(e) => setAddPrice(e.target.value)} inputProps={{ min: 0, step: 0.01 }} />
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addItemPrice} disabled={busy}>Set price</Button>
            <Box sx={{ flex: 1 }} />
            <TextField size="small" label="Search items" value={search} onChange={(e) => setSearch(e.target.value)} />
          </Stack>
          {itemsLoading ? <CircularProgress size={20} /> : (
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Item</TableCell><TableCell>Description</TableCell><TableCell align="right">Price</TableCell><TableCell align="right">Save</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && (
                  <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">No item prices in this list{search ? ' for that search' : ''}.</Typography></TableCell></TableRow>
                )}
                {items.map((it) => {
                  const editing = priceEdits[it.itemCode];
                  const baseVal = it.price != null ? String(it.price) : '';
                  const val = editing !== undefined ? editing : baseVal;
                  const dirty = editing !== undefined && editing !== baseVal;
                  return (
                    <TableRow key={it.itemCode}>
                      <TableCell>{it.itemCode}</TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{it.itemName}</Typography></TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={val} sx={{ width: 110 }}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                          onChange={(e) => setPriceEdits((p) => ({ ...p, [it.itemCode]: e.target.value }))} />
                        {it.currency ? <Typography variant="caption" sx={{ ml: 0.5 }}>{it.currency}</Typography> : null}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Save to SAP"><span>
                          <IconButton size="small" color="primary" disabled={!dirty || busy} onClick={() => savePrice(it.itemCode)}><SaveIcon fontSize="small" /></IconButton>
                        </span></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Searchable SAP price-list picker for the FLOOR_PRICE_PRICELIST_NO setting.
// Stores the selected PriceListNo (as a string) as the setting value.
function PriceListSettingField({ value, options, loading, onChange }: {
  value: string;
  options: PriceListOpt[];
  loading: boolean;
  onChange: (v: string) => void;
}) {
  const current = String(value ?? '');
  // Keep the stored value visible even if it isn't in the fetched list (stale/unknown).
  const opts = current && !options.some((p) => String(p.priceListNo) === current)
    ? [{ priceListNo: Number(current) || 0, priceListName: '(current value)' }, ...options]
    : options;
  const selected = opts.find((p) => String(p.priceListNo) === current) ?? null;
  const label = (o: PriceListOpt) => (o.priceListName ? `${o.priceListName} (${o.priceListNo})` : String(o.priceListNo));
  return (
    <Autocomplete
      size="small"
      fullWidth
      options={opts}
      value={selected}
      loading={loading}
      onChange={(_, v) => onChange(v ? String(v.priceListNo) : '')}
      getOptionLabel={label}
      isOptionEqualToValue={(a, b) => String(a.priceListNo) === String(b.priceListNo)}
      renderOption={(props, o) => <li {...props} key={o.priceListNo}>{label(o)}</li>}
      noOptionsText={loading ? 'Loading…' : 'No price lists'}
      renderInput={(params) => (
        <TextField {...params} placeholder="Search price list…" InputProps={{
          ...params.InputProps,
          endAdornment: (<>{loading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>),
        }} />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Picker for LAST_PRICE_PRECEDENCE. A value already stored that isn't one of the
// listed combinations stays selectable so opening this page can't silently
// rewrite a hand-tuned precedence.
function PrecedenceSettingField({ value, onChange }: {
  value: string;
  onChange: (v: string) => void;
}) {
  const current = String(value ?? '').toUpperCase();
  const opts = current && !PRICE_PRECEDENCE_OPTIONS.some((o) => o.value === current)
    ? [{ value: current, label: `${current} (current value)` }, ...PRICE_PRECEDENCE_OPTIONS]
    : PRICE_PRECEDENCE_OPTIONS;
  return (
    <FormControl size="small" fullWidth>
      <Select
        value={current || PRICE_PRECEDENCE_OPTIONS[0].value}
        onChange={(e) => onChange(String(e.target.value))}
        renderValue={(v) => <code>{String(v)}</code>}
      >
        {opts.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            <Box>
              <Typography variant="body2">{o.label}</Typography>
              <Typography variant="caption" color="text.secondary"><code>{o.value}</code></Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default SalesPortalSettings;
